/**
 * Project Service
 * Handles CRUD operations for projects via the backend API
 */

let PROJECTS_API_URL = ""

async function loadApiUrl(): Promise<string> {
  if (PROJECTS_API_URL) {
    return PROJECTS_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    const baseUrl = config.feedbackApiUrl
    if (!baseUrl) {
      throw new Error("API URL not found in aws-exports.json")
    }
    PROJECTS_API_URL = `${baseUrl.replace(/\/?$/, "/")}projects`
    return PROJECTS_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Projects API URL not configured")
  }
}

async function fetchWithAuth(url: string, idToken: string, options: RequestInit = {}) {
  const response = await fetch(url, {
    ...options,
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
  }

  return response.json()
}

export interface KnowledgeFile {
  fileKey: string
  filename: string
  contentType: string
  uploadedAt: string
}

export interface ProjectSummary {
  projectId: string
  name: string
  description: string
  knowledgeFileCount: number
  createdAt: string
  updatedAt: string
}

export interface ProjectDetail {
  projectId: string
  name: string
  description: string
  customInstructions: string
  knowledgeFiles: KnowledgeFile[]
  createdAt: string
  updatedAt: string
}

export interface CreateProjectData {
  name: string
  description?: string
  customInstructions?: string
}

export interface UpdateProjectData {
  name?: string
  description?: string
  customInstructions?: string
}

export async function listProjects(idToken: string): Promise<ProjectSummary[]> {
  const apiUrl = await loadApiUrl()
  const data = await fetchWithAuth(apiUrl, idToken)
  return data.projects || []
}

export async function createProject(idToken: string, data: CreateProjectData): Promise<ProjectDetail> {
  const apiUrl = await loadApiUrl()
  return fetchWithAuth(apiUrl, idToken, {
    method: "POST",
    body: JSON.stringify(data),
  })
}

export async function getProject(idToken: string, projectId: string): Promise<ProjectDetail> {
  const apiUrl = await loadApiUrl()
  return fetchWithAuth(`${apiUrl}/${projectId}`, idToken)
}

export async function updateProject(
  idToken: string,
  projectId: string,
  data: UpdateProjectData
): Promise<void> {
  const apiUrl = await loadApiUrl()
  await fetchWithAuth(`${apiUrl}/${projectId}`, idToken, {
    method: "PUT",
    body: JSON.stringify(data),
  })
}

export async function deleteProject(idToken: string, projectId: string): Promise<void> {
  const apiUrl = await loadApiUrl()
  await fetchWithAuth(`${apiUrl}/${projectId}`, idToken, {
    method: "DELETE",
  })
}

export async function getKnowledgeFileUploadUrl(
  idToken: string,
  projectId: string,
  filename: string,
  contentType: string
): Promise<{ uploadUrl: string; fileKey: string }> {
  const apiUrl = await loadApiUrl()
  return fetchWithAuth(`${apiUrl}/${projectId}/knowledge-files`, idToken, {
    method: "POST",
    body: JSON.stringify({ filename, contentType }),
  })
}

export async function deleteKnowledgeFile(
  idToken: string,
  projectId: string,
  fileKey: string
): Promise<void> {
  const apiUrl = await loadApiUrl()
  await fetchWithAuth(`${apiUrl}/${projectId}/knowledge-files`, idToken, {
    method: "DELETE",
    body: JSON.stringify({ fileKey }),
  })
}
